import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import { createSuperTeachers, getRoles, getSchools } from "./services";

//i18n
import { withTranslation } from "react-i18next";

const getDefaultRoleId = roles => {
    const preferredRole = roles.find(role => Number(role.id) === 8);
    return `${preferredRole?.id || roles[0]?.id || ""}`;
};

const getEmptyUser = roleId => ({
    full_name: "",
    email: "",
    school_code: "",
    role_id: roleId || "8",
    default_password: true
});

const ModalAdd = (props) => {
    const [users, setUsers] = useState([getEmptyUser()]);
    const [schools, setSchools] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [successAlert, setSuccessAlert] = useState(false);
    const [errorAlert, setErrorAlert] = useState(false);

    const getDropdownData = async () => {
        try {
            const [schoolResponse, roleResponse] = await Promise.all([
                getSchools(),
                getRoles()
            ]);
            const schoolData = schoolResponse || [];
            const roleData = roleResponse || [];
            const defaultRoleId = getDefaultRoleId(roleData);

            setSchools(schoolData);
            setRoles(roleData);
            setUsers(currentUsers => currentUsers.map(user => ({
                ...user,
                role_id: user.role_id || defaultRoleId
            })));
        } catch (error) {
            setErrorAlert(true);
        }
    };

    useEffect(() => {
        if(props.modalShow) {
            getDropdownData();
        }
    }, [props.modalShow]);

    const resetForm = () => {
        setUsers([getEmptyUser(getDefaultRoleId(roles))]);
        setValidationMessage("");
    };

    const handleUserChange = (index, field, value) => {
        setUsers(currentUsers => currentUsers.map((user, userIndex) => {
            if(userIndex !== index) return user;
            return {
                ...user,
                [field]: value
            };
        }));
    };

    const addUserInput = () => {
        setUsers(currentUsers => [
            ...currentUsers,
            getEmptyUser(getDefaultRoleId(roles))
        ]);
    };

    const removeUserInput = index => {
        setUsers(currentUsers => currentUsers.filter((user, userIndex) => userIndex !== index));
    };

    const validateUsers = () => {
        const incompleteRow = users.find(user => (
            !user.full_name.trim() ||
            !user.email.trim() ||
            !user.school_code ||
            !user.role_id
        ));

        if(incompleteRow) {
            setValidationMessage("Full Name, Email, School, dan Role wajib diisi.");
            return false;
        }

        return true;
    };

    const uploadDataUser = async event => {
        event.preventDefault();
        if(!validateUsers()) return;

        const body = {
            data: users.map(user => ({
                full_name: user.full_name,
                email: user.email,
                school_code: user.school_code,
                default_password: true,
                role_id: Number(user.role_id)
            }))
        };

        try {
            setLoading(true);
            await createSuperTeachers(body.data);
            setSuccessAlert(true);
            props.onCreated?.();
        } catch (error) {
            setErrorAlert(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        props.toggleModal();
    };

    return(
        <>
            <Modal
                size="lg"
                centered={true}
                isOpen={props.modalShow}
                toggle={handleClose}
            >
                <ModalHeader toggle={handleClose}>Add User</ModalHeader>
                <ModalBody>
                    <Form onSubmit={uploadDataUser}>
                        <div className="alert alert-info py-2 mb-3">
                            Default password will be generated with value <strong>teacher123</strong>.
                        </div>
                        {users.map((user, index) => (
                            <div key={`user-input-${index}`} className="border rounded p-3 mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">User {index + 1}</h6>
                                    {users.length > 1 ?
                                        <Button
                                            type="button"
                                            color="danger"
                                            className="btn-sm"
                                            onClick={() => removeUserInput(index)}
                                        >
                                            Remove
                                        </Button>
                                    : null}
                                </div>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for={`full_name_${index}`}>Full Name</Label>
                                            <Input
                                                id={`full_name_${index}`}
                                                name={`full_name_${index}`}
                                                type="text"
                                                onChange={event => handleUserChange(index, "full_name", event.target.value)}
                                                value={user.full_name}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for={`email_${index}`}>Email</Label>
                                            <Input
                                                id={`email_${index}`}
                                                name={`email_${index}`}
                                                type="email"
                                                onChange={event => handleUserChange(index, "email", event.target.value)}
                                                value={user.email}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for={`school_code_${index}`}>School</Label>
                                            <Input
                                                id={`school_code_${index}`}
                                                name={`school_code_${index}`}
                                                type="select"
                                                onChange={event => handleUserChange(index, "school_code", event.target.value)}
                                                value={user.school_code}
                                            >
                                                <option value="">Select School</option>
                                                {schools.map(school => (
                                                    <option key={school.id} value={school.school_code}>
                                                        {school.name}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for={`role_id_${index}`}>Role</Label>
                                            <Input
                                                id={`role_id_${index}`}
                                                name={`role_id_${index}`}
                                                type="select"
                                                onChange={event => handleUserChange(index, "role_id", event.target.value)}
                                                value={user.role_id}
                                            >
                                                <option value="">Select Role</option>
                                                {roles.map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.description || role.name}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                        {validationMessage ?
                            <div className="text-danger mb-3">{validationMessage}</div>
                        : null}
                        <Row>
                            <Col md="12">
                                <div className="d-flex justify-content-between">
                                    <Button
                                        type="button"
                                        color="primary"
                                        className="btn-sm m-1"
                                        onClick={addUserInput}
                                    >
                                        Add Input
                                    </Button>
                                    <div>
                                        <Button className="btn-sm" color="danger" type="button" onClick={handleClose}>
                                            Cancel
                                        </Button>
                                        <Button className="btn-sm mx-1" color="primary" type="submit" disabled={loading}>
                                            {loading ? (
                                                <>
                                                    <i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i> Loading
                                                </>
                                            ) : "Simpan"}
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
            </Modal>

            {successAlert ?
                <SweetAlert
                    title="Good job!"
                    confirmBtnText="Yes"
                    success
                    confirmBtnBsStyle="primary"
                    onConfirm={() => {
                        setSuccessAlert(false);
                        handleClose();
                    }}
                >
                    Data User has been created successfully
                </SweetAlert>
            : null}

            {errorAlert ?
                <SweetAlert
                    title="Oppss!"
                    confirmBtnText="Yes"
                    error
                    confirmBtnBsStyle="primary"
                    onConfirm={() => setErrorAlert(false)}
                >
                    Something wrong, please try again
                </SweetAlert>
            : null}
        </>
    )
}

ModalAdd.propTypes = {
    toggleModal: PropTypes.any,
    modalShow: PropTypes.any,
    onCreated: PropTypes.any
};

export default withTranslation()(ModalAdd);
